import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "@/store";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  ArrowRightIcon,
  DotsHorizontalIcon,
  Pencil1Icon,
  TrashIcon,
} from "@radix-ui/react-icons";
import CollectionService from "@/services/CollectionService";
import { toast } from "sonner";
import posthog from "posthog-js";
import { getFlagForLanguage } from "@/utils/languageFlags";

const accentPalette = [
  {
    gradient: "from-primary/30 via-primary/10 to-transparent",
    border: "border-primary/40",
    chip: "text-primary/80",
    shadow:
      "shadow-panel hover:shadow-glow",
  },
  {
    gradient: "from-primary/30 via-primary/10 to-transparent",
    border: "border-primary/40",
    chip: "text-primary/80",
    shadow:
      "shadow-panel hover:shadow-glow",
  },
  {
    gradient: "from-primary/25 via-primary/10 to-transparent",
    border: "border-primary/40",
    chip: "text-primary/80",
    shadow:
      "shadow-panel hover:shadow-glow",
  },
];

const CollectionItem = ({
  title,
  contentType,
  language,
  onPractice,
  onEdit,
  onDelete,
  onClick,
  index,
}) => {
  const accent = accentPalette[index % accentPalette.length];
  const languageFlag = getFlagForLanguage(language);

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      onClick={onClick}
      className={`group relative flex h-full cursor-pointer select-none transform-gpu flex-col justify-between overflow-hidden rounded-3xl border bg-surface/80 p-5 text-left transition-all duration-200 ease-out hover:-translate-y-1.5 hover:bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas sm:p-6 ${accent.border} ${accent.shadow}`}
    >
      <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
        <div
          className={`absolute inset-0 bg-gradient-to-br ${accent.gradient}`}
        />
      </div>

      <div className="relative flex flex-1 flex-col gap-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-3">
            <h2 className="text-2xl font-semibold leading-snug text-fg md:text-[28px]">
              {title}
            </h2>
            {contentType === "youtube" && (
              <span className="inline-flex w-fit items-center gap-1 rounded-full bg-primary/12 px-2.5 py-[2px] text-[10px] font-semibold uppercase tracking-[0.25em] text-fg shadow-panel">
                🎥 Video
              </span>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="relative z-10 rounded-full border border-line bg-elevated p-2 text-fg transition-colors duration-200 ease-out hover:border-primary/40 hover:bg-elevated focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
                onClick={(event) => event.stopPropagation()}
              >
                <DotsHorizontalIcon className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={12}
              className="relative w-[232px] overflow-hidden rounded-[26px] border border-primary/25 bg-surface p-0 text-sm text-fg shadow-panel backdrop-blur-2xl before:pointer-events-none before:absolute before:inset-0 before:rounded-[26px] before:border before:border-primary/5 before:opacity-70"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="relative space-y-1 p-2">
                <DropdownMenuItem
                  className="group flex items-center justify-between gap-6 rounded-2xl px-4 py-3 text-[15px] font-medium text-fg transition-all data-[highlighted]:bg-surface/80 data-[highlighted]:text-fg"
                  onClick={(event) => {
                    event.stopPropagation();
                    onPractice();
                  }}
                >
                  <span className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-fg transition-colors group-hover:border-primary/30 group-data-[highlighted]:border-primary/50 group-data-[highlighted]:bg-primary/15">
                      <ArrowRightIcon className="h-4 w-4" />
                    </span>
                    Practice
                  </span>
                  <span className="text-xs uppercase tracking-[0.35em] text-primary/80">
                    Go
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="group flex items-center justify-between gap-6 rounded-2xl px-4 py-3 text-[15px] font-medium text-fg transition-all data-[highlighted]:bg-surface/80 data-[highlighted]:text-fg"
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit();
                  }}
                >
                  <span className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-fg transition-colors group-hover:border-primary/30 group-data-[highlighted]:border-primary/50 group-data-[highlighted]:bg-primary/15">
                      <Pencil1Icon className="h-4 w-4" />
                    </span>
                    Edit
                  </span>
                  <span className="text-xs uppercase tracking-[0.35em] text-fg-muted">
                    Open
                  </span>
                </DropdownMenuItem>
              </div>
              <DropdownMenuSeparator className="mx-3 h-px bg-primary/15" />
              <DropdownMenuItem
                className="group mx-2 mb-2 mt-1 flex items-center justify-between gap-6 rounded-2xl px-4 py-3 text-[15px] font-medium text-danger transition-all data-[highlighted]:bg-danger/10 data-[highlighted]:text-danger"
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete();
                }}
              >
                <span className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-danger/30 bg-danger/10 text-danger transition-colors group-hover:border-danger/60 group-data-[highlighted]:border-danger/60 group-data-[highlighted]:bg-danger/20">
                    <TrashIcon className="h-4 w-4" />
                  </span>
                  Delete
                </span>
                <span className="text-xs uppercase tracking-[0.35em] text-danger/70">
                  Remove
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="relative mt-8 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span
            className="flex items-center justify-center text-2xl drop-shadow-panel"
            role="img"
            aria-label={`${language || "unknown"} language flag`}
            title={language ? `${language} flag` : "Language flag"}
          >
            {languageFlag}
          </span>
        </div>
        <Button
          variant="ghost"
          className="rounded-full border border-line bg-elevated px-4 py-2 text-sm font-medium text-fg transition-all duration-200 ease-out hover:-translate-y-0.5 hover:bg-elevated focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          onClick={(event) => {
            event.stopPropagation();
            onPractice();
          }}
        >
          <span className="flex items-center gap-2">
            Practice
            <ArrowRightIcon className="h-4 w-4" />
          </span>
        </Button>
      </div>
    </div>
  );
};

const Collections = ({
  collections: initialCollections,
  onCollectionClick,
  onCollectionsChange,
}) => {
  const navigate = useNavigate();
  const { user, setCurrentCollectionId } = useStore().user;
  const [collections, setCollections] = useState(initialCollections);

  useEffect(() => {
    setCollections(initialCollections);
  }, [initialCollections]);

  const handlePractice = async (collectionId) => {
    try {
      await setCurrentCollectionId(collectionId);
      navigate("/");
    } catch (error) {
      console.error("Failed to set current collection ID:", error);
    }
  };

  const handleEdit = (collectionId) => {
    onCollectionClick(collectionId);
  };

  const handleDelete = async (collectionId) => {
    try {
      await CollectionService.deleteCollection(collectionId);
      toast("Collection deleted", {
        description: "The collection has been successfully deleted!",
        action: {
          label: "Close",
          onClick: () => {
            /* noop */
          },
        },
      });
      posthog.capture("collection_deleted");

      const updatedCollections = collections.filter(
        (collection) => collection.id !== collectionId
      );
      setCollections(updatedCollections);
      onCollectionsChange?.(updatedCollections);

      if (user.current_collection_id === collectionId) {
        const newCurrentCollectionId =
          updatedCollections.length > 0 ? updatedCollections[0].id : null;
        setCurrentCollectionId(newCurrentCollectionId);
      }
    } catch (error) {
      console.error("Failed to delete collection:", error);
    }
  };

  if (!collections || collections.length === 0) {
    return (
      <div className="flex min-h-[240px] flex-col items-center justify-center gap-4 text-center text-fg-muted">
        <div className="rounded-full border border-line bg-surface/80 px-4 py-1 text-xs uppercase tracking-[0.3em] text-fg-muted">
          No Collections Yet
        </div>
        <p className="max-w-xs text-sm text-fg-muted">
          Start a collection to keep the content you love close at hand. Your
          practice session will feel right at home here.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
      {collections.map((collection, index) => (
        <CollectionItem
          key={collection.id}
          index={index}
          title={collection.title}
          contentType={collection.content_type}
          language={collection.language}
          onPractice={() => handlePractice(collection.id)}
          onEdit={() => handleEdit(collection.id)}
          onDelete={() => handleDelete(collection.id)}
          onClick={() => onCollectionClick(collection.id)}
        />
      ))}
    </div>
  );
};

export default Collections;
